/*
 *  This document is the CONFIDENTIAL PROPERTY of Corero Network Security, Inc.
 *  Copyright (C) 2018-2025 by Corero Network Security, Inc. All rights reserved.
 *  Notice of copyright on this document does not constitute publication.
 */

import validator from 'validator';
import _ from 'lodash';
import { Address6 } from 'ip-address';
import { getIpObject } from './IPUtils';
import Messages from "./messages";
import { currentUtcMoment, utcMoment } from "../utils/dateUtils";

class ValidationRules {
    constructor(defaultValidator) {
        this.validator = defaultValidator;
    }

    UNKNOWN_FIELD_LABEL = 'This field';
    UNKNOWN_VALUE_LABEL = 'This value';

    isEmail = (email) => {
        return this.validator.isEmail(email) ? null : 'This is not a valid email';
    };

    isNotSupportEmail = (email) => {
        return email.toLowerCase() === 'support@corero.com' ?
            'This email address cannot be used' :
            null;
    };

    isUrl = (url) => {
        return this.validator.isURL(url, {
            require_protocol: true,
            require_valid_protocol: true
        }) ? null : 'This is not a valid url';
    };

    minLength = (field, length, fieldName = 'Value') => {
        if (field && field.length < length)
            return `${fieldName} must be more than ${length} characters`;
        else
            return null;
    };

    maxLength = (field, length, fieldName = 'Value') => {
        if (field && field.length > length)
            return `${fieldName} must be no more than ${length} characters`;
        else
            return null;
    };

    max = (field, number, fieldName = this.UNKNOWN_VALUE_LABEL, errorMsg = `${fieldName} should be less than or equal to ${number}`) => {
        if (typeof (number) === "string") {
            number = parseInt(number);
        }
        if (!isNaN(field) && field <= number) {
            return null;
        } else {
            return errorMsg;
        }
    };

    min = (field, number, fieldName = this.UNKNOWN_VALUE_LABEL, errorMsg = `${fieldName} should be greater than or equal to ${number}`) => {
        if (typeof (number) === "string") {
            number = parseInt(number);
        }
        if (!isNaN(field) && field >= number) {
            return null;
        } else {
            return errorMsg;
        }
    };

    isNonNegativeNumber = (field, fieldName = this.UNKNOWN_VALUE_LABEL) => {
        if (!isNaN(field) && field >= 0 && field <= Number.MAX_VALUE) {
            return null;
        } else {
            return `${fieldName} should be 0 or a positive number`;
        }
    };

    repeatOnValidation = (min, max, type, field) => {
        const isInDayRange = this.min(field, min) === null && this.max(field, max) === null;

        return isInDayRange ? null : `Please enter a ${type} value between ${min} and ${max}`;
    };

    validatePlaceholders = (text, validPlaceholders) => {

        if (!text || !validPlaceholders) {
            return true;
        }

        const regex = /{[^}]*}/gm;
        const placeholders = text.match(regex);
        let isValidPlaceholder = true;
        let closedState = true;

        for (let i = 0; i < text.length; i++) {
            const currentSymbol = text[i];

            if ((closedState && currentSymbol === "}") || (!closedState && currentSymbol === "{")) {
                isValidPlaceholder = false;
                break;
            } else if (currentSymbol === "{") {
                closedState = false;
            } else if (currentSymbol === "}") {
                closedState = true;
            }
        }

        if (!closedState) {
            isValidPlaceholder = false;
        }

        placeholders !== null && placeholders.forEach(ph => {
            const parsedPh = ph.substring(1, ph.length - 1);

            if (validPlaceholders.indexOf(parsedPh) === -1) {
                isValidPlaceholder = false;
            }
        });

        return isValidPlaceholder ? null : 'Invalid placeholders';
    };

    /**
     * Checks if given field has a selection
     * @param select select field
     * @param fieldName optional identifying name of the field
     * @returns {null|string} error message or null, if field has selection
     */
    isSelected = (select, fieldName = this.UNKNOWN_FIELD_LABEL) => {
        return select && select !== "" && select !== 0 && select !== "0" ? null : `${fieldName} is required`;
    };

    /**
     * Checks if input <ul>
     *     <li> is valid json
     *     <li> does not have given values as object keys
     *     <li> contains given values
     * </ul>
     *
     * Note that this method will return on first failure of those conditions in order
     *
     * @param input string to test
     * @param valuesToContain required values
     * @returns {string|null|*} null, if valid. Error message otherwise
     */
    isValidJsonContainingValues = (input, valuesToContain) => {
        try {
            const jsonObject = JSON.parse(input);

            const errorMessage = this.checkForKeyContaining(jsonObject, valuesToContain);
            if (!!errorMessage) {
                return errorMessage;
            }

            // check for missing values

            const missingValues = [];
            // can directly search in input string, as we already have made sure the values are not object keys
            valuesToContain.forEach(value => {
                    if (!input.includes(value)) {
                        missingValues.push(value);
                    }
                }
            );
            if (missingValues.length > 0) {
                return `JSON values must include the following value(s) ${missingValues.join(",")}`
            }
            return null;
        } catch (e) {
            return 'Invalid JSON';
        }
    };

    /**
     * Returns an error message, if the given object contains any of the given search terms within any keys of
     * the object
     *
     * @param object object to test
     * @param searchTerms list of search terms that are not allowed as keys
     * @returns {string|null|*} error message or null, if there is no validation error
     */
    checkForKeyContaining = (object, searchTerms) => {
        const values = Object.values(object);
        const keys = Object.keys(object);
        for (const searchTerm of searchTerms) {
            if (keys.findIndex(key => key.includes(searchTerm)) !== -1) {
                return `Placeholder value: ${searchTerm} is not allowed in any key of the JSON object`;
            }
        }
        for (const value of values) {
            if (Array.isArray(value)) {
                for (const elem of value) {
                    const res = this.checkForKeyContaining(elem, searchTerms);
                    if (!!res) {
                        return res;
                    }
                }
            } else if (typeof value === "object") {
                return this.checkForKeyContaining(value, searchTerms);
            }

        }
        return null;
    }

    matchPassword = (confirmedPassword) => {
        return confirmedPassword['password'] === confirmedPassword['confirmPassword'] ? null : "Passwords do not match";
    };

    isValidPassword = (field, regex, errorMessage) => {
        if (!field || !field.match(new RegExp(regex.replace("/\\/g", "\\")))) {
            return errorMessage;
        }
        return null;
    };

    isIp = (field) => {
        //old regex
        //let rx=/^((?![a-zA-Z]).)*$/;
        const rx = /^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])$/;
        // /^(?!.*\.$)((?!0\d)(1?\d?\d|25[0-5]|2[0-4]\d)(\.|$)){4}$/
        if (rx.test(field) === false) {
            return "You have entered an invalid IP address!";
        }
        return null;
    };

    /**
     * Validates that the given field is a valid IP address or FQDN
     *
     * @param field the field
     * @returns {string|null} Error message if validation fails; null otherwise
     */
    isIpOrFqdn = (field) => {
        const errorMsg = 'Please enter a valid IP address or Fully Qualified Domain Name';
        if (!field) {
            return errorMsg;
        }
        // The library doesn't consider 'localhost' a FQDN, but we allow it
        return field === 'localhost' || this.validator.isIP(field) || this.validator.isFQDN(field) ?
            null :
            errorMsg;
    }

    isNonNegativeInteger = (field) => {
        const rx = /^\d+$/;
        if (rx.test(field) === false) {
            return "Please enter either 0 or a positive integer value";
        }
        return null;
    };

    isIntegerInRange = (value, min, max, fieldName = "Field", inclusive = true) => {
        const errorMsg = `${fieldName} must be an integer value between ${min} and ${max} ${inclusive ? '(inclusive)' : '(exclusive)'}`;
        const rx = /^-?\d+$/;
        if (rx.test(value) === false) {
            return errorMsg;
        }
        const valueInt = _.parseInt(value, 10);
        if (inclusive && (valueInt < min || valueInt > max)) {
            return errorMsg;
        }
        if (!inclusive && (valueInt <= min || valueInt >= max)) {
            return errorMsg;
        }
        return null;
    };

    range = (value, min, max, fieldName) => {
        if (_.parseInt(value, 10) < min || _.parseInt(value, 10) > max) {
            return `Please enter a valid ${fieldName} value between ${min} and ${max}.`
        }
        return null;
    };

    isValidName = (name) => {
        const pattern = /^[A-Za-z .\-&()_0-9]+$/;
        const errorMessage = 'Name must only include alphanumerics, spaces .-&()_ symbols.';
        return this.isValidStringValue(name, pattern, 64, errorMessage, 'Name');
    };

    isValidPropertyName = (name) => {
        const pattern = /^[a-z.\-_]+$/;
        const errorMessage = 'Name must only include lowercase-letters, ".", "-" or "_"';
        return this.isValidStringValue(name, pattern, 64, errorMessage, 'Name');
    };

    /**
     * Checks if the provided name fits the CMS's ObjectName pattern and length.
     * @param name {string} The string to be checked.
     * @param fieldName {string} The noun used to describe the field in error messages.
     * @returns {string|null} Error string if error, null otherwise.
     */
    isValidCMSObjectName = (name, fieldName) => {
        const pattern = /^[A-Za-z .\-&()_0-9/@:=]+$/;
        const errorMessage = `${fieldName} must only include alphanumerics, spaces .-&()_/@:= symbols.`;
        return this.isValidStringValue(name, pattern, 64, errorMessage, fieldName);
    };

    /**
     * Checks that the value provided isn't empty, fits the pattern provided and
     * is less than the specified number of characters in length.
     * @param value {string} The value to be checked
     * @param pattern {RegExp} The pattern the value is going to be compared against.
     * @param maxLength {int} The max length acceptable for the string.
     * @param patternFailureMessage {string} The error message to be returned if the string fails to match the pattern.
     * @param fieldName {string} The noun used to describe the field in error messages.
     * @returns {string|null} Error string if error, null otherwise.
     */
    isValidStringValue = (value, pattern, maxLength, patternFailureMessage, fieldName) => {
        const requiredError = this.required(value);
        const tooLongError = this.maxLength(value, maxLength, fieldName);
        if (requiredError) {
            return requiredError;
        }
        if (tooLongError) {
            return tooLongError;
        }
        if (!value.match(pattern)) {
            return patternFailureMessage;
        }
        return null;
    };

    required = (value, fieldName = this.UNKNOWN_FIELD_LABEL) => {
        if (value !== "" && value !== null && value !== undefined) {
            return null;
        }
        return `${fieldName} is required`;
    };

    /**
     * Validates if the value is empty or contains whitespaces
     * @param value {string}     the value to be validated.
     * @param fieldName optional field name to identify field
     * @return error message if condition is met.
     */
    isNotHtmlWhiteSpaceOnly = (value, fieldName = this.UNKNOWN_FIELD_LABEL) => {
        if (value && !value.replace(/(<([^>]+)>)/ig, '').replace(/&([^;]+);/gm, '').trim().length) {
            return `${fieldName} is either empty or only contains whitespaces`;
        }
        return null;
    };

    /**
     * Validates if the value is empty or contains whitespaces
     * @param value {string}     the value to be validated.
     * @return error message if condition is met.
     */
    isNotBlankOrWhiteSpaceOnly = (value) => {
        if (!value.replace(/\s/g, '').length) {
            return "The field is either empty or only contains whitespaces.";
        }
    };

    /**
     * Checks if given value has leading or trailing whitespaces
     * @param value given value
     * @returns {null|string} error message or null if value is valid
     */
    isTrimmed = (value) => {
        if (!!value && value !== value.trim()) {
            return "This field contains leading or trailing whitespaces."
        }
        return null;
    };

    /**
     * Will validate entered value does not contain white space
     * @param value Input
     * @returns {string} Error, or null otherwise
     */
    doesNotContainWhiteSpace = (value) => {
        if (/\s/g.test(value)) {
            return "The field cannot contain white space such as spaces and tabs";
        }
    }

    /**
     * Will validate entered value against IPv4/IPV6/CIDR/IPV4-IPV4 range/IPv6-IPv6 range
     * @param {string} value : the ip to be validated
     * @param {boolean} canBeEmpty : indicates if an empty string is valid
     * @returns {string,null} : Returns null if valid, or an error string if not valid
     */
    isValidIpEntry = (value, canBeEmpty = false) => {
        if (canBeEmpty && [null, undefined, ""].includes(value)) {
            return null;
        }
        if (typeof value !== "string" || value === "") {
            return Messages.EMPTY_VALUE();
        }
        const IPs = value.split("-").map(x => x.trim());
        if (IPs.length > 2) {
            // there are too many IPs
            return Messages.TWO_ITEMS_IN_RANGE();
        } else if (IPs.length === 2) {
            // its a range
            if (IPs[0].includes('/') || IPs[1].includes('/')) {
                return Messages.CIDRS_NOT_VALID_IN_RANGE();
            }
            const ipa = getIpObject(IPs[0]);
            if(ipa === null){
                return  Messages.INVALID_IP(IPs[0]);
            }
            const ipb = getIpObject(IPs[1]);
            if(ipb === null){
                return  Messages.INVALID_IP(IPs[1]);
            }
            if(ipa.v4 !== ipb.v4){
                return Messages.MIX_OF_IP_VERSIONS();
            }
            if (ipa.v4 === false) {
                //expand input value for comparison
                const ipc = new Address6(ipa.canonicalForm());
                const ipd = new Address6(ipb.canonicalForm());
                if (ipc.bigInteger().compareTo(ipd.bigInteger()) > 0) {
                    //return shorthand addresses in error message4
                    return Messages.INVALID_IPS_GT(ipa.address, ipb.address);

                }
            } else if (ipa.bigInteger().compareTo(ipb.bigInteger()) >0) {
                // its ipv4
                return Messages.INVALID_IPS_GT(ipa.address, ipb.address);
            }


        } else {
            //must be a single IP/CIDR
            const ip = getIpObject(IPs[0]);
            if(ip === null){
                return  Messages.INVALID_IP(IPs[0]);
            }
            if (value.includes('/')) {
                //its a cidr
                const cidrSplit = value.split('/');
                if (cidrSplit.length > 2) {
                    return Messages.TOO_MANY_CIDR_MASKS();
                }
                let startValue = cidrSplit[0];
                if (ip.v4 === false) {
                    //expand input value for comparison
                    startValue = new Address6(startValue).canonicalForm();
                }
                //we check if the first address in the cidr matches the host of the ip address
                //we don't need to check the validity of the mask as the ip object would not be valid if was incorrect
                if (ip.startAddress().address !== startValue) {
                    return Messages.INVALID_CIDR();
                }
            }
        }
        return null;
    }

    /**
     * Checks if a value is in a Set, if it is then return. If editMode is true; it will first check against
     * initialValue to allow for the user to set the original value for the input
     * @param value {string} - value to check
     * @param otherValues {Set<string>} - other values to compare against
     * @param message {string} - message to show on validation failure , if not supplied, "Field must be unique" is used
     * @param editMode {boolean} - if true, validation will pass if the value is the same as the initialValue
     * @param initialValue {string} - original value of the input, compared against value when editMode is true
     */
    isUnique(value, otherValues, message='', editMode=false, initialValue='') {
        // Allow the user to keep the same value when in edit mode
        if (editMode && value === initialValue) {
            return null;
        }
        return otherValues.has(value) ? (message || 'Field must be unique') : null;
    }

    /**
     * Runs the supplied validation function only if the value is defined.
     * @param validationFunction the validation function to run.
     *    This will be passed the validationFunctionArguments as its arguments.
     * @param value the value. Is passed to the sole argument to validationFunction if
     *    validationFunctionArguments is undefined.
     * @param validationFunctionArguments an array containing the arguments passed to
     *    the validationFunction. Either this must be undefined or value must be included
     *    in this array to be passed to the validationFunction, as Default is [value].
     * @returns {string|null} the validation message from the supplied function or null if the value
     *    is not defined.
     */
    ifTrue = (
        validationFunction,
        value,
        validationFunctionArguments = [value],
    ) => (value ? validationFunction(...validationFunctionArguments) : null);

    /**
     * Validates an OpenID Connect (OIDC) scope
     * @param csvValues Comma seperated string of values
     * @returns {string|null} Error message if validation fails; null otherwise
     */
    isValidOIDCScope = (csvValues) => {
        if (csvValues && csvValues.length > 0) {
            const pattern = /^[A-Za-z!#$%&'"()*+_.\-0-9/:;<=>?@[\]^`{|}~]+$/;
            const values = csvValues.split(",").map(item => item.trim());
            if (values.some(value => !value.match(pattern))) {
                return 'Each value must only include alphanumerics and !#$%&\'"()*+-./[]^_`{|}~ symbols.';
            }
        }
    };

    isDateTimeAfter = (field, futureNumber, futureChronoUnit) => {
        let minMoment = currentUtcMoment().add(futureNumber, futureChronoUnit);
        let input = utcMoment(field);
        if(input.isBefore(minMoment)) {
            return `This date/time must be at least ${futureNumber} ${futureChronoUnit} in the future`
        }
    }

    /**
     * Validates that the given array has at least one element
     *
     * @param array    the array
     * @param errorMsg the error message
     * @returns {string|null} Error message if validation fails; null otherwise
     */
    hasAtLeastOneItem = (array, errorMsg = 'At least one value must be specified') => {
        if (Array.isArray(array) && array?.length === 0) {
            return errorMsg;
        }
    }

    /**
     * Validates that the given input is a valid XML document
     *
     * @param input    the input
     * @param errorMsg the error message
     * @returns {string|null} Error message if validation fails; null otherwise
     */
    isValidXML = (input, errorMsg = 'Please enter valid XML') => {
        if (!input || typeof input !== 'string') {
            return errorMsg;
        }

        const trimmedValue = input.trim();

        if (trimmedValue === '') {
            return errorMsg;
        }

        try {
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(trimmedValue, 'text/xml');
            const parserError = xmlDoc.getElementsByTagName('parsererror');
            if (parserError.length > 0) {
                return errorMsg;
            }
            // Ensure the document has at least one element
            return !xmlDoc.documentElement ? errorMsg : null;
        } catch (error) {
            return errorMsg;
        }
    }
}

const validationRules = new ValidationRules(validator);

export default validationRules;
